import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import dayjs from "dayjs";

import { mapDispatchProps, mapStateToProps } from "store/store";
import { ROUTES } from "routes";
import { useLanguage } from "@languages/index";
import { queries } from "api/api.constants";
import { CampaignDto } from "api/repositories/campaign/campaign.types";
import { BrandDto } from "api/repositories/brand/brand.types";
import { CAMPAIGN_PAYMENT_DTO_ENUMS } from "api/repositories/campaignPayments/campaignPayments.constants";
import { CampaignPaymentDto, GetCampaignPaymentsGroupedByCampaignRequestParams } from "api/repositories/campaignPayments/campaignPayments.types";
import { ProductDto } from "api/repositories/product/product.types";
import * as HELPER_CURRENCY from "helpers/currency"
import CampaignPaymentHistoryItem from "components/campaignPaymentHistoryItem/index";
import Paginator from "components/paginator";
import TextInputField from "components/textInput";
import InputDate from "components/inputDate";
import Button from "components/button";
import Select, { SELECT_ON_CHANGE_MODES } from "components/select";
import ProductListModal from "./components/productListModal";
import LoadingIndicator from "components/loadingIndicator";

interface SearchForm {
  page: number;
  productName: string;
  durationType: GetCampaignPaymentsGroupedByCampaignRequestParams['durationType'];
  startDt: string;
  endDt: string;
}

const CampaignPaymentIndex = (props:any) => {
  const navigate = useNavigate();
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.campaigns.payments.groupedByCampaign }

  const isMaster = props.auth?.isMaster

  const DURATION_TYPE_OPTIONS:{value: GetCampaignPaymentsGroupedByCampaignRequestParams['durationType'], name: string}[] = [
    { value: "CREATED_AT"                , name: languageTexts.index_duration_type_option_1 },
    { value: "TAX_INVOICE_SCHEDULED_DATE", name: languageTexts.index_duration_type_option_2 },
    { value: "PAID_WITH_CARD_DATE"       , name: languageTexts.index_duration_type_option_3 },
  ]

  /****************************************************************************
   * STATES AND HOOKS
   **************************************************************************/
  const [expandedCampaigns, setExpandedCampaigns] = useState<{[key: string]: boolean}>({});
  const searchFormMethods = useForm<SearchForm>({
    defaultValues: {
      page: 1,
      productName: '',
      durationType: 'CREATED_AT',
      startDt: '',
      endDt: '',
    },
  });
  const [durationType] = useWatch({
    control: searchFormMethods.control,
    name: ['durationType'],
  });

  // ReactQuery1
  const [queryParams, setQueryParams] = useState<SearchForm>(searchFormMethods.getValues());
  const {
    data: campaignPaymentsGroupedByCampaign,
    refetch: refetchCampaignPaymentsGroupedByCampaign,
    isLoading,
    isFetching,
  } = useQuery({
    ...queries.campaignPayments.getCampaignPaymentsByCampaign({
      brandToken: props.auth?.token,
      ...queryParams,
    }),
    select: (data: any) => data?.data,
    enabled: (props.auth?.token != null),
  });

  /****************************************************************************
   * FUNCTIONS
   ****************************************************************************/

  const onSubmit = () => {
    setQueryParams(searchFormMethods.getValues());
    setExpandedCampaigns({});
    refetchCampaignPaymentsGroupedByCampaign();
  }

  const onToggleCampaignExpand = (campaignId: number) => {
    setExpandedCampaigns(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
  };

  const onPageChange = (page: number) => {
    searchFormMethods.setValue('page', page);
    onSubmit();
  }
  
  /****************************************************************************
   * RENDER
   ****************************************************************************/

  const makeCampaignName = (campaign:Partial<CampaignDto>, products:Partial<ProductDto>[]) => {
    if (products == null || products.length === 0) {
      return `${campaign.id}`;
    }

    const deletedTag = campaign.deletedAt ? `[${languageTexts.delete}] ` : '';

    const productsUsing = products.filter((product) => product.isUse === 1)
    const mainProductName = (productsUsing.length > 0) ? productsUsing[0].name 
      : products[0].name;

    const productCount = products.length

    return deletedTag + mainProductName + (productCount > 1 ? languageTexts.index_text_4(productCount - 1) : '')
  }

  const makePriceTotalOfCampaign = (campaignPayments:Partial<CampaignPaymentDto>[]) => {
    const VAT_RATE = 0.1;
    return campaignPayments.reduce((acc, campaignPayment) => {
      let sign = 1;

      if (campaignPayment.type.includes('REFUND')) {
        sign = -1;
      }

      if ([
        CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.MONTHLY,
        CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.YEARLY,
      ].includes(campaignPayment.type as any)) {
        return acc + sign * campaignPayment.price * (1 + VAT_RATE); // DB priceTotal에 수량이 곱해져 있다. 
      }

      return acc + sign * campaignPayment.priceTotal
    }, 0).toLocaleString()
  }

  const makeCampaignPaymentsCurrencyText = (campaignPayments:Partial<CampaignPaymentDto>[]) => {
    if (campaignPayments.length === 0) {
      return '';
    }
    let currency = campaignPayments[0].currency;
    if (currency == null) {
      currency = props.brand.currency;
    }
    return HELPER_CURRENCY.getCurrencySuffixByCurrency(currency);
  }


  const showProductListModal = (products:Partial<ProductDto>[]) => {
    props.showModal({
      content: () => (
        <ProductListModal
          products={products}
        />
      ),
    })
  }


  const renderLoadingIndicator = () => {
    if (!isLoading && !isFetching) return null;

    const hasData = campaignPaymentsGroupedByCampaign?.campaigns?.length > 0;

    return (
      <LoadingIndicator
        colorSet={hasData ? 'black' : 'white'}
      />
    );
  }

  const renderPageTitle = () => {
    return (
      <div className="flex flex-row justify-between mt-12 mb-2 lg:mb-9">  
        <span className="font-bold text-2xl tracking-tighter">{languageTexts.index_text_1}</span>
        {/* <button className="text-[14px] underline"
          onClick={() => navigate(ROUTES.CAMPAIGN_PAYMENTS_V1_INDEX.PATH)}>
          {`결제 시간 순으로 보기`}
        </button> */}
      </div>
    )
  }


  const renderSearchPanel = () => {
    return (
      <div className="mx-auto mt-5 mb-4 w-full flex flex-col gap-[10px]">
        <div>
          <TextInputField
            className="py-0 h-full"
            inputClassName="flex justify-center px-3 py-3 h-full text-left text-sm tracking-tighter"
            bordered
            register={searchFormMethods.register('productName', {})}
            placeholder={languageTexts.product_name}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="w-full md:w-auto md:min-w-[100px]">
            <Controller
              control={searchFormMethods.control}
              name="durationType"
              render={({ field }) => (
                <Select
                  onChangeMode={SELECT_ON_CHANGE_MODES.VALUE}
                  options={DURATION_TYPE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                  className="h-full"
                  inputFieldContainerStyle={{ height: '100%' }}
                  selectClassName={"py-3 px-2 pr-10 text-sm md:text-[14px]"}
                  bordered
                />
              )}
            />
          </div>
          <div className="flex-grow flex flex-row items-center gap-2">
            <div className="flex-grow">
              <Controller
                control={searchFormMethods.control}
                name="startDt"
                render={({ field }) => (
                  <InputDate
                    className="flex-1"
                    placeholder={languageTexts.start_date}
                    // minDate={minStartDate}
                    // maxDate={maxDate}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="flex-grow-0">
              <span className="tracking-tighter leading-[40px] select-none">{` ~ `}</span>
            </div>

            <div className="flex-grow">
              <Controller
                control={searchFormMethods.control}
                name="endDt"
                render={({ field }) => (
                  <InputDate
                    className="flex-1"
                    placeholder={languageTexts.end_date}
                    // minDate={minDate}
                    // maxDate={maxEndDate}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <Button
              className={"flex-initial h-full w-[64px] flex items-center justify-center select-none"}
              text={languageTexts.inquiry}
              textClassName={"text-[14px]"}
              buttonColor={"BLACK"}
              textColor={"WHITE"}
              justify={"center"}
              onClick={onSubmit}
            />
          </div>

          {durationType === 'TAX_INVOICE_SCHEDULED_DATE' && (
            <div className="text-[12px] md:text-[14px] text-[#999]">
              {languageTexts.index_text_3}
            </div>
          )}
        </div>
      </div>
    )
  }


  const renderHistoriesWithoutCampaign = () => {
    const payments = campaignPaymentsGroupedByCampaign?.paymentsWithoutCampaign ?? [];
    if (payments.length === 0) {
      return null;
    }

    const dummyCampaignId = 0;
    const currency = makeCampaignPaymentsCurrencyText(payments);
    return (
      <div
        key={dummyCampaignId}
        data-campaign-id={dummyCampaignId}
        className="pb-[4px]"
      >
        <div
          className={classNames([
            "px-[20px] py-[8px]",
            "flex items-center gap-[8px]",
            "cursor-pointer bg-gray-100 hover:bg-gray-100 text-[12px] md:text-[14px] rounded-[4px]"
          ])}
          onClick={() => onToggleCampaignExpand(dummyCampaignId)}
        >
          <div style={{ flex: '1 1 auto'}}>
            <div className="pt-[4px] w-full whitespace-pre-wrap">
              {languageTexts.etc}
            </div>
          </div>
          <div style={{ flex: '0 0 1'}}>
            <div className="whitespace-nowrap">
              {languageTexts._price(makePriceTotalOfCampaign(payments), currency)}
            </div>
          </div>
          <div style={{ flex: '0 0 1'}}>
            {expandedCampaigns[dummyCampaignId] ? '▼' : '▶'}
          </div>
        </div>
        {expandedCampaigns[dummyCampaignId] && (
          <div className="pb-6">
            {payments.map((campaignPayment) => {
              return (
                <CampaignPaymentHistoryItem
                  key={`campaignPayment-${campaignPayment.id}`}
                  history={campaignPayment}
                />
              );
            })}
          </div>
        )}
      </div>
    )
  }


  const renderHistories = () => {
    return (
      <>
        <div className="flex flex-col gap-[4px]">
          {campaignPaymentsGroupedByCampaign?.campaigns.map((
            campaign:Partial<CampaignDto> & {
              brand:Partial<BrandDto>,
              campaignPayments:Partial<CampaignPaymentDto>[],
              products:Partial<ProductDto>[],
            }) => {
              const currency = makeCampaignPaymentsCurrencyText(campaign.campaignPayments);
              return (
                <div key={campaign.id} data-campaign-id={campaign.id}>
                  <div
                    className={classNames([
                      "px-[20px] py-[8px]",
                      "flex items-center gap-[8px]",
                      "cursor-pointer bg-gray-100 hover:bg-gray-100 text-[12px] md:text-[14px] rounded-[4px]"
                    ])}
                    onClick={() => onToggleCampaignExpand(campaign.id)}
                  >
                    <div style={{ flex: '1 1 auto'}}>
                      <div className="pt-[4px] w-full whitespace-pre-wrap">
                        {makeCampaignName(campaign, campaign.products)}
                        {isMaster && (
                          <span
                            className="ml-2 text-[12px] text-red-500 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation()
                              showProductListModal(campaign.products);
                            }}>
                              ?
                            </span>
                        )}
                      </div>
                      <div className="text-[12px] text-[#999999]">
                        {`${dayjs(campaign.createdAt).format('YY.MM.DD')} ${languageTexts.index_text_2}`}
                      </div>
                    </div>
                    <div style={{ flex: '0 0 1'}}>
                      <div className="whitespace-nowrap">
                        {languageTexts._price(makePriceTotalOfCampaign(campaign.campaignPayments), currency)}
                      </div>
                    </div>
                    <div style={{ flex: '0 0 1'}}>
                      {expandedCampaigns[campaign.id] ? '▼' : '▶'}
                    </div>
                  </div>
                  {expandedCampaigns[campaign.id] && (
                    <div className="pb-6">
                      {campaign?.campaignPayments.map((campaignPayment) => {
                        return (
                          <CampaignPaymentHistoryItem
                            key={`campaignPayment-${campaignPayment.id}`}
                            history={campaignPayment}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )
            }
          )}
        </div>
        <Paginator
          lastPage={campaignPaymentsGroupedByCampaign?.lastPage}
          currentPage={campaignPaymentsGroupedByCampaign?.currentPage}
          onPageChange={(page) => onPageChange(page)}
          offset={5}
        />
      </>
    )
  }
    

  return (
    <>
      <div className="xl:px-10 px-5 pb-10 mx-auto w-full max-w-[800px] mb-[48px]">
        {renderPageTitle()}
        {renderSearchPanel()}
        <div className="mt-[30px] relative min-h-[100px]">
          {renderLoadingIndicator()}
          {renderHistoriesWithoutCampaign()}
          {renderHistories()}
        </div>
      </div>
    </>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(CampaignPaymentIndex);
